// src/pages/ProfilePage.js
import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import {
    Box, Button, Container, Typography, Paper, Grid, TextField,
    Avatar, Badge, IconButton, CircularProgress, Alert, Divider,
    Chip, Snackbar
} from '@mui/material';
import { Edit as EditIcon, PhotoCamera } from '@mui/icons-material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import useAuth from '../hooks/useAuth'; 

const customTheme = createTheme({ palette: { primary: { main: '#ff834f' } } });

const ProfilePage = () => {
    // Obtenha o usuário, login e revalidateAuth do useAuth
    // Usaremos authUser para exibir, e authLogin para atualizar o token globalmente
    const { user: authUser, login: authLogin, revalidateAuth } = useAuth(); 
    
    // Estado local para o formulário e preview (inicializado com dados do authUser)
    const [localName, setLocalName] = useState('');
    const [localEmail, setLocalEmail] = useState('');
    const [localAvatarPreview, setLocalAvatarPreview] = useState('');

    const [loading, setLoading] = useState(true); // Controla o loading inicial da página
    const [infoSaving, setInfoSaving] = useState(false); // Loading para salvar informações
    const [passwordSaving, setPasswordSaving] = useState(false); // Loading para salvar senha
    const [avatarUploading, setAvatarUploading] = useState(false); // Loading para upload de avatar

    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const fileInputRef = useRef(null);

    // Efeito para carregar os dados do perfil quando o authUser estiver disponível
    useEffect(() => {
        // Se authUser não for nulo (já carregado pelo contexto)
        if (authUser) {
            setLocalName(authUser.name || '');
            setLocalEmail(authUser.email || '');
            if (authUser.avatarUrl) {
                // Certifique-se de que o caminho da URL da imagem está correto para o frontend
                setLocalAvatarPreview(`http://localhost:5001/uploads/${authUser.avatarUrl.replace(/\\/g, '/')}`);
            } else {
                setLocalAvatarPreview(''); // Limpa o preview se não houver avatar
            }
            setLoading(false); // Termina o loading inicial
        } else {
            // Se authUser for nulo (contexto ainda carregando ou não logado),
            // podemos tentar buscar os dados diretamente se necessário,
            // mas o ideal é que ProtectedRoute já garanta o authUser.
            // Para simplicidade, vamos apenas manter o loading se authUser for null.
            setLoading(true); // Mantém o loading se authUser ainda não está disponível
        }
    }, [authUser]); // Depende do authUser do contexto

    const handleInfoSubmit = async (e) => {
        e.preventDefault();
        setInfoSaving(true); // Inicia o loading para salvar informações
        const formData = new FormData(e.currentTarget);
        const updatedName = formData.get('name');
        const updatedEmail = formData.get('email');

        try {
            const response = await api.put('/profile', { name: updatedName, email: updatedEmail });
            setSnackbar({ open: true, message: 'Informações atualizadas com sucesso!', severity: 'success' });
            
            // Se o backend retorna um novo token com nome/email atualizados, use-o
            if (response.data.token) {
                authLogin(response.data.token); // Atualiza o token e o user no contexto
            } else {
                // Fallback: Se o backend NÃO retorna um novo token, mas os dados foram atualizados no DB,
                // force uma revalidação do contexto para pegar os dados frescos.
                revalidateAuth(); 
            }
            
        } catch (err) {
            console.error('Erro ao atualizar informações:', err.response?.data || err.message);
            setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao atualizar informações.', severity: 'error' });
        } finally {
            setInfoSaving(false); // Finaliza o loading
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setPasswordSaving(true); // Inicia o loading para salvar senha
        const formData = new FormData(e.currentTarget);
        const currentPassword = formData.get('currentPassword');
        const newPassword = formData.get('newPassword');

        if(newPassword.length < 6) {
            setSnackbar({ open: true, message: 'A nova senha deve ter pelo menos 6 caracteres.', severity: 'error' });
            setPasswordSaving(false);
            return;
        }

        try {
            await api.put('/profile/password', { currentPassword, newPassword });
            setSnackbar({ open: true, message: 'Senha alterada com sucesso!', severity: 'success' });
            e.target.reset(); // Limpa os campos da senha
        } catch (err) {
            console.error('Erro ao alterar senha:', err.response?.data || err.message);
            setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao alterar senha.', severity: 'error' });
        } finally {
            setPasswordSaving(false); // Finaliza o loading
        }
    };

    const handleAvatarFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Cria um URL temporário para o preview imediato no frontend
            setLocalAvatarPreview(URL.createObjectURL(file)); 
            // Dispara o upload automaticamente após a seleção do arquivo
            handleAvatarUpload(file); 
        }
    };

    const handleAvatarUpload = async (file) => {
        setAvatarUploading(true); // Inicia o loading para upload de avatar
        const formData = new FormData();
        formData.append('avatar', file);

        try {
            const response = await api.post('/profile/avatar', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            
            console.log('Upload de avatar bem-sucedido:', response.data);

            // *** PASSO CRUCIAL: ATUALIZA O TOKEN NO CONTEXTO DE AUTENTICAÇÃO ***
            // O backend agora retorna um novo token com o avatarUrl atualizado.
            // Chamamos authLogin para que o useAuth salve o novo token e redefina o user no contexto.
            if (response.data.token) {
                authLogin(response.data.token); 
            } else {
                // Fallback: Se por algum motivo o token não vier, force a revalidação
                revalidateAuth();
            }

            setSnackbar({ open: true, message: 'Foto de perfil atualizada!', severity: 'success' });

        } catch (err) {
            console.error('Erro ao enviar foto:', err.response?.data || err.message);
            setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao enviar foto.', severity: 'error' });
            // Se houver erro, reverta o preview para o avatar original do usuário
            if (authUser?.avatarUrl) {
                setLocalAvatarPreview(`http://localhost:5001/uploads/${authUser.avatarUrl.replace(/\\/g, '/')}`);
            } else {
                setLocalAvatarPreview('');
            }
        } finally {
            setAvatarUploading(false); // Finaliza o loading
        }
    };

    const handleCloseSnackbar = (event, reason) => { if (reason === 'clickaway') return; setSnackbar({ ...snackbar, open: false }); };

    // Exibir carregador inicial se o authUser ainda não estiver disponível
    if (loading || !authUser) { // Adicionado !authUser para garantir que o contexto carregou
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /><Typography sx={{ml: 2}}>Carregando perfil...</Typography></Box>;
    }

    return (
        <ThemeProvider theme={customTheme}>
            <Box sx={{ width: '100%', bgcolor: '#F7F9FC', py: 4 }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" fontWeight={700} sx={{ mb: 4 }}>Meu Perfil</Typography>
                    
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', borderRadius: 4, height: '100%' }}>
                                <Badge
                                    overlap="circular"
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    badgeContent={
                                        <IconButton onClick={() => fileInputRef.current.click()} sx={{ bgcolor: 'background.paper', p: 1, border: '1px solid #ddd' }} disabled={avatarUploading}>
                                            {avatarUploading ? <CircularProgress size={20} /> : <PhotoCamera />}
                                        </IconButton>
                                    }
                                >
                                    <Avatar
                                        sx={{ width: 120, height: 120, mb: 2, fontSize: '3rem' }}
                                        src={localAvatarPreview || ''} // Usa o preview local
                                        alt={authUser.name ? authUser.name[0].toUpperCase() : 'U'}
                                    >
                                        {!localAvatarPreview && authUser.name ? authUser.name[0].toUpperCase() : ''}
                                    </Avatar>
                                </Badge>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    hidden
                                    accept="image/*"
                                    onChange={handleAvatarFileChange}
                                />
                                <Typography variant="h6" fontWeight={600}>{authUser.name}</Typography>
                                <Typography color="text.secondary">{authUser.email}</Typography>
                                <Chip label={authUser.role} color="primary" sx={{ mt: 2 }} />
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <Paper sx={{ p: 3, borderRadius: 4, mb: 4 }}>
                                <Typography variant="h6" fontWeight={600} gutterBottom>Informações do Perfil</Typography>
                                <Box component="form" onSubmit={handleInfoSubmit}>
                                    <TextField name="name" label="Nome Completo" value={localName} onChange={(e) => setLocalName(e.target.value)} fullWidth margin="normal" />
                                    <TextField name="email" label="Email" value={localEmail} fullWidth margin="normal" InputProps={{ readOnly: true }} />
                                    <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={infoSaving}>
                                        {infoSaving ? <CircularProgress size={24} /> : 'Salvar Informações'}
                                    </Button>
                                </Box>
                            </Paper>
                            <Paper sx={{ p: 3, borderRadius: 4 }}>
                                <Typography variant="h6" fontWeight={600} gutterBottom>Alterar Senha</Typography>
                                <Box component="form" onSubmit={handlePasswordSubmit}>
                                    <TextField name="currentPassword" label="Senha Atual" type="password" fullWidth required margin="normal" />
                                    <TextField name="newPassword" label="Nova Senha" type="password" fullWidth required margin="normal" />
                                    <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={passwordSaving}>
                                        {passwordSaving ? <CircularProgress size={24} /> : 'Alterar Senha'}
                                    </Button>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </ThemeProvider>
    );
};

export default ProfilePage;
